<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-container">
      <div class="error-icon">📚</div>
      <h1 class="error-title">Archive Under Maintenance</h1>
      <p class="error-message">
        The Ephemera Archive is temporarily reorganizing its collection. Our
        archivists are working diligently to restore full access.
      </p>
      <div class="error-details" v-if="errorMessage">
        <p class="details-label">Technical Details:</p>
        <p class="details-text">{{ errorMessage }}</p>
      </div>
      <button @click="retry" class="retry-button">
        <span class="button-icon">🔄</span>
        Try Reopening the Archive
      </button>
      <p class="help-text">
        If this issue persists, please refresh the page or visit again later.
      </p>
    </div>
  </div>
  <slot v-else></slot>
</template>

<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue';

const hasError = ref(false);
const errorMessage = ref<string>('');

// Capture errors from child components
onErrorCaptured((error: Error) => {
  console.error('Error boundary caught:', error);
  hasError.value = true;
  errorMessage.value = error.message || 'An unexpected error occurred';
  return false; // Prevent error from propagating
});

function retry() {
  hasError.value = false;
  errorMessage.value = '';
  // Reload the page to reset state
  window.location.reload();
}
</script>

<style scoped>
.error-boundary {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-sepia-light);
  padding: var(--spacing-xl);
}

.error-container {
  max-width: 600px;
  text-align: center;
  background-color: white;
  padding: var(--spacing-2xl);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 2px solid var(--color-sepia-medium);
}

.error-icon {
  font-size: 4rem;
  margin-bottom: var(--spacing-lg);
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.error-title {
  font-family: var(--font-serif);
  font-size: 2rem;
  color: var(--color-burgundy);
  margin-bottom: var(--spacing-md);
}

.error-message {
  font-size: 1.1rem;
  color: var(--color-black-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
}

.error-details {
  background-color: var(--color-sepia-light);
  padding: var(--spacing-md);
  border-radius: 6px;
  margin-bottom: var(--spacing-lg);
  text-align: left;
}

.details-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-black-primary);
  margin-bottom: var(--spacing-xs);
}

.details-text {
  font-size: 0.875rem;
  color: var(--color-black-secondary);
  font-family: monospace;
  word-break: break-word;
}

.retry-button {
  background-color: var(--color-burgundy);
  color: white;
  border: none;
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  transition: all 0.2s ease;
  margin-bottom: var(--spacing-md);
}

.retry-button:hover {
  background-color: var(--color-museum-red);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(139, 26, 26, 0.3);
}

.retry-button:active {
  transform: translateY(0);
}

.button-icon {
  font-size: 1.2rem;
}

.help-text {
  font-size: 0.875rem;
  color: var(--color-black-secondary);
  font-style: italic;
}

@media (max-width: 768px) {
  .error-container {
    padding: var(--spacing-lg);
  }

  .error-title {
    font-size: 1.5rem;
  }

  .error-message {
    font-size: 1rem;
  }
}
</style>
